import { useEffect } from "react";

type EnhancedTable = {
  wrapper: HTMLElement;
  table: HTMLTableElement;
  topScrollbar: HTMLDivElement;
  spacer: HTMLDivElement;
  resizeObserver: ResizeObserver;
  dispose: () => void;
};

const findOuterScroller = (element: HTMLElement): HTMLElement | null => {
  let current = element.parentElement;
  while (current) {
    const style = window.getComputedStyle(current);
    if (/(auto|scroll)/.test(style.overflowY) && current.scrollHeight > current.clientHeight + 1) {
      return current;
    }
    current = current.parentElement;
  }
  return document.scrollingElement instanceof HTMLElement ? document.scrollingElement : null;
};

/**
 * Chuẩn hóa thao tác cho toàn bộ bảng mà không yêu cầu từng mô-đun tự cài đặt:
 * - thêm thanh cuộn ngang ở phía trên và đồng bộ với bảng;
 * - để con lăn tiếp tục cuộn cửa sổ ngoài khi bảng đã tới đầu/cuối;
 * - tự đưa dòng vừa thêm vào vùng nhìn thấy và đặt con trỏ ở ô đầu tiên.
 */
export function TableInteractionEnhancer() {
  useEffect(() => {
    const enhanced = new Map<HTMLElement, EnhancedTable>();

    const removeEntry = (wrapper: HTMLElement) => {
      const entry = enhanced.get(wrapper);
      if (!entry) return;
      entry.dispose();
      entry.resizeObserver.disconnect();
      entry.topScrollbar.remove();
      delete wrapper.dataset.tableScrollEnhanced;
      enhanced.delete(wrapper);
    };

    const updateEntry = (entry: EnhancedTable) => {
      if (!entry.wrapper.isConnected || !entry.table.isConnected) {
        removeEntry(entry.wrapper);
        return;
      }
      const width = Math.ceil(entry.table.scrollWidth);
      entry.spacer.style.width = `${width}px`;
      entry.topScrollbar.hidden = width <= Math.ceil(entry.wrapper.clientWidth) + 1;
      entry.topScrollbar.scrollLeft = entry.wrapper.scrollLeft;
    };

    const enhance = (wrapper: HTMLElement) => {
      if (enhanced.has(wrapper)) return;
      const table = wrapper.querySelector<HTMLTableElement>(":scope > table.data-table");
      if (!table) return;

      const topScrollbar = document.createElement("div");
      topScrollbar.className = "table-top-scrollbar";
      topScrollbar.setAttribute("aria-label", "Cuộn ngang bảng");
      const spacer = document.createElement("div");
      spacer.className = "table-top-scrollbar-spacer";
      topScrollbar.append(spacer);
      // Đặt thanh cuộn trong chính vùng bảng để nó có một dải riêng và không
      // đè lên dòng dữ liệu đầu tiên khi được giữ cố định.
      wrapper.prepend(topScrollbar);
      wrapper.dataset.tableScrollEnhanced = "true";

      let syncing = false;
      const syncFromTop = () => {
        if (syncing) return;
        syncing = true;
        wrapper.scrollLeft = topScrollbar.scrollLeft;
        syncing = false;
      };
      const syncFromTable = () => {
        if (syncing) return;
        syncing = true;
        topScrollbar.scrollLeft = wrapper.scrollLeft;
        syncing = false;
      };
      topScrollbar.addEventListener("scroll", syncFromTop, { passive: true });
      wrapper.addEventListener("scroll", syncFromTable, { passive: true });

      const continueOuterScroll = (event: WheelEvent) => {
        if (Math.abs(event.deltaY) <= Math.abs(event.deltaX) || wrapper.scrollHeight <= wrapper.clientHeight + 1) {
          return;
        }
        const atTop = wrapper.scrollTop <= 0;
        const atBottom = wrapper.scrollTop + wrapper.clientHeight >= wrapper.scrollHeight - 1;
        if ((event.deltaY < 0 && !atTop) || (event.deltaY > 0 && !atBottom)) return;

        const outer = findOuterScroller(wrapper);
        if (!outer || outer === wrapper) return;
        event.preventDefault();
        outer.scrollBy({ top: event.deltaY, behavior: "auto" });
      };
      wrapper.addEventListener("wheel", continueOuterScroll, { passive: false });

      const resizeObserver = new ResizeObserver(() => {
        const entry = enhanced.get(wrapper);
        if (entry) updateEntry(entry);
      });
      const entry: EnhancedTable = {
        wrapper,
        table,
        topScrollbar,
        spacer,
        resizeObserver,
        dispose: () => {
          topScrollbar.removeEventListener("scroll", syncFromTop);
          wrapper.removeEventListener("scroll", syncFromTable);
          wrapper.removeEventListener("wheel", continueOuterScroll);
        }
      };
      enhanced.set(wrapper, entry);
      resizeObserver.observe(wrapper);
      resizeObserver.observe(table);
      updateEntry(entry);
    };

    const scan = () => {
      document.querySelectorAll<HTMLElement>(".data-table-wrapper").forEach(enhance);
      [...enhanced.values()].forEach(updateEntry);
    };

    let scanFrame = 0;
    const scheduleScan = () => {
      cancelAnimationFrame(scanFrame);
      scanFrame = requestAnimationFrame(scan);
    };
    const mutationObserver = new MutationObserver(scheduleScan);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", scheduleScan);
    scan();

    const handleAddLine = (event: MouseEvent) => {
      const button = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>(".add-line-below button");
      const footer = button?.closest<HTMLElement>(".add-line-below");
      if (!button || !footer || button.disabled) return;

      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!footer.isConnected) return;
        const content = footer.previousElementSibling as HTMLElement | null;
        const row = content?.querySelector<HTMLElement>(
          "tbody tr:last-child, .contact-card:last-child, .form-grid:last-child"
        );
        if (!row) return;
        if (content?.classList.contains("data-table-wrapper")) {
          content.scrollTo({ top: content.scrollHeight, behavior: "smooth" });
        }
        row.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        window.setTimeout(() => {
          const input = row.querySelector<HTMLElement>(
            ".search-select input:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])"
          );
          input?.focus({ preventScroll: true });
        }, 180);
      }));
    };
    document.addEventListener("click", handleAddLine, true);

    return () => {
      document.removeEventListener("click", handleAddLine, true);
      window.removeEventListener("resize", scheduleScan);
      mutationObserver.disconnect();
      cancelAnimationFrame(scanFrame);
      [...enhanced.keys()].forEach(removeEntry);
    };
  }, []);

  return null;
}
